"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Book, Zap, Droplets, Shield, Coins } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function MarseilleMinorArcanaPage() {
  const suits = [
    {
      name: "Bâtons",
      enName: "Wands/Rods",
      element: "Ateş",
      description: "Enerji, tutku, yaratıcılık ve girişim",
      icon: <Zap className="h-6 w-6 text-white" />,
      color: "from-red-500 to-orange-700",
      path: "batons",
      keywords: ["Girişimcilik", "İlham", "Büyüme", "Hareket"]
    },
    {
      name: "Épées",
      enName: "Swords",
      element: "Hava",
      description: "Düşünce, çatışma, zorluklar ve zihinsel güç",
      icon: <Shield className="h-6 w-6 text-white" />,
      color: "from-gray-500 to-slate-700",
      path: "epees",
      keywords: ["Zihin", "Çatışma", "Adalet", "İletişim"]
    },
    {
      name: "Coupes",
      enName: "Cups",
      element: "Su",
      description: "Duygular, sevgi, ilişkiler ve ruhsal bağlantı",
      icon: <Droplets className="h-6 w-6 text-white" />,
      color: "from-blue-500 to-indigo-700",
      path: "coupes",
      keywords: ["Aşk", "Duygular", "Sezgi", "İlişkiler"]
    },
    {
      name: "Deniers",
      enName: "Pentacles/Coins",
      element: "Toprak",
      description: "Para, maddi durum, sağlık ve pratik konular",
      icon: <Coins className="h-6 w-6 text-white" />,
      color: "from-green-500 to-emerald-700",
      path: "deniers",
      keywords: ["Para", "Sağlık", "İş", "Maddi durum"]
    }
  ];

  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Minör Arkana</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Tarot Sistemleri
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/marseille"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Tarot de Marseille
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">Minör Arkana</span>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-red-900/20 to-orange-900/20 rounded-xl p-8 mb-8 border border-red-500/20">
          <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
            <Book className="h-10 w-10 text-red-400" />
            Marseille Minör Arkana
          </h1>
          <p className="text-lg text-slate-200 leading-relaxed">
            56 karttan oluşan Minör Arkana, günlük yaşamın detaylarını ve dört elementin enerjilerini temsil eder.
            Marseille geleneğinde pip kartları sade geometrik desenlerle sunulur ve okuyucunun sezgisine dayanır.
          </p>
        </div>

        {/* Marseille Characteristics */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Minör Arkana Özellikleri</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Görsel Yaklaşım</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Pip kartları sadece sembolik objeler gösterir</li>
                <li>• Detaylı sahneler yok, soyut desenler var</li>
                <li>• Sezgisel yorumlama gerektir</li>
                <li>• Court kartları karakteristik figürler</li>
              </ul>
            </div>
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Okuma Yaklaşımı</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Numerolojik anlamlar ön planda</li>
                <li>• Element özelliklerine odaklanma</li>
                <li>• Geleneksel kart anlamları</li>
                <li>• Okuyucunun deneyimi kritik</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Four Suits */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Dört Suit</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {suits.map((suit, index) => (
              <div key={index} className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-red-500/50 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 bg-gradient-to-br ${suit.color} rounded-lg flex items-center justify-center`}>
                    {suit.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-100">{suit.name}</h3>
                    <p className="text-sm text-slate-400">({suit.enName}) - {suit.element} elementi</p>
                  </div>
                </div>
                <p className="text-slate-300 mb-4">{suit.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {suit.keywords.map((keyword, i) => (
                    <span key={i} className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs">
                      {keyword}
                    </span>
                  ))}
                </div>
                <Link
                  href={`/about/tarot/systems/marseille/minor-arcana/${suit.path}`}
                  className={`inline-flex items-center px-4 py-2 bg-gradient-to-r ${suit.color} text-white rounded-lg transition-colors text-sm hover:shadow-lg`}
                >
                  <Book className="h-4 w-4 mr-2" />
                  {suit.name} Kartlarını Gör
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Card Structure */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Kart Yapısı</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-4">Pip Kartları (As-10)</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  <span className="text-sm text-slate-400">As (1) - Suit'in saf potansiyeli</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  <span className="text-sm text-slate-400">2-10 - Artan karmaşıklık ve gelişim</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  <span className="text-sm text-slate-400">Her kart numerolojik anlamı taşır</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  <span className="text-sm text-slate-400">Soyut geometrik desenler</span>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-4">Court Kartları</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  <span className="text-sm text-slate-400">Valet - Gençlik, başlangıç enerjisi</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  <span className="text-sm text-slate-400">Chevalier - Hareket, eylem</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  <span className="text-sm text-slate-400">Dame - Dişil enerji, alma</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  <span className="text-sm text-slate-400">Roi - Eril enerji, verme</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reading Approaches */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Okuma Yaklaşımları</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Numerolojik</h3>
              <p className="text-slate-300 text-sm mb-3">
                Kart numaralarının anlamlarına odaklanır.
              </p>
              <ul className="text-xs text-slate-400 space-y-1">
                <li>• 1: Başlangıç, potansiyel</li>
                <li>• 5: Değişim, çatışma</li>
                <li>• 10: Tamamlanma, döngü sonu</li>
              </ul>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Elemental</h3>
              <p className="text-slate-300 text-sm mb-3">
                Suit elementlerinin etkilerine bakar.
              </p>
              <ul className="text-xs text-slate-400 space-y-1">
                <li>• Ateş: Enerji ve hareket</li>
                <li>• Su: Duygular ve ilişkiler</li>
                <li>• Hava: Düşünce ve iletişim</li>
                <li>• Toprak: Maddi durum</li>
              </ul>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Sezgisel</h3>
              <p className="text-slate-300 text-sm mb-3">
                Kartın uyandırdığı içgüdülere güvenir.
              </p>
              <ul className="text-xs text-slate-400 space-y-1">
                <li>• Görsel basitlik sezgiyi açar</li>
                <li>• Kişisel sembolik bağlantılar</li>
                <li>• Deneyim ile gelişen anlayış</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Comparison */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille vs Rider-Waite Minör Arkana</h2>
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-100 mb-3">Marseille Yaklaşımı</h3>
                <ul className="space-y-2 text-slate-300 text-sm">
                  <li>• Pip kartları sadece sembol sayısını gösterir</li>
                  <li>• Okuyucunun yorumlama becerisi kritik</li>
                  <li>• Geleneksel numerolojik anlamlar</li>
                  <li>• Sade ama güçlü etki</li>
                  <li>• Profesyonel okuyucular için ideal</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-100 mb-3">Rider-Waite Yaklaşımı</h3>
                <ul className="space-y-2 text-slate-300 text-sm">
                  <li>• Her pip kartında detaylı sahne</li>
                  <li>• Görsel ipuçları anlamları verir</li>
                  <li>• Yeni başlayanlar için kolay</li>
                  <li>• Allegorik anlatım</li>
                  <li>• Modern tarot öğretiminde standart</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel Marseille tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/major-arcana"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Majör Arkana
          </Link>

          <Link
            href="/about/tarot/systems/marseille"
            className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            Tarot de Marseille
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}